import styled from "styled-components";
import {
  Brain,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Code2,
  Compass,
  FileJson,
  MessageSquareMore,
  NotebookPen,
  Sparkles,
  Users,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const Page = styled.div`
  min-height: 100vh;
  padding: 120px 20px 80px;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

const Hero = styled.section`
  padding: 32px;
  border-radius: 32px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.86), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 18px 0 0;
  font-size: clamp(38px, 5vw, 60px);
  font-weight: 1000;
  line-height: 1.04;
  color: #f8fafc;
  max-width: 860px;
`;

const Sub = styled.p`
  margin-top: 16px;
  max-width: 820px;
  font-size: 18px;
  line-height: 1.85;
  color: #94a3b8;
`;

const CTARow = styled.div`
  margin-top: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const PrimaryCTA = styled.button`
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 900;
  color: #04111f;
  background: linear-gradient(135deg, #f59e0b, #facc15);
`;

const SecondaryCTA = styled(Link)`
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 14px;
  font-weight: 900;
  color: #e2e8f0;
  background: rgba(15, 23, 42, 0.44);
`;

const Section = styled.section`
  margin-top: 34px;
  padding: 28px;
  border-radius: 28px;
  background: rgba(15, 23, 42, 0.62);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: #f8fafc;
  font-size: 28px;
  font-weight: 1000;
`;

const SectionText = styled.p`
  margin-top: 10px;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.8;
  max-width: 860px;
`;

const Grid = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 18px;
`;

const Card = styled.div`
  padding: 18px;
  border-radius: 20px;
  background: rgba(2, 6, 23, 0.42);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #f8fafc;
  font-weight: 900;
`;

const IconWrap = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  color: ${({ $tone = "#7dd3fc" }) => $tone};
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const CardText = styled.p`
  margin-top: 12px;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.75;
`;

const BulletList = styled.ul`
  margin: 14px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 10px;

  li {
    display: flex;
    gap: 10px;
    color: #cbd5e1;
    line-height: 1.6;
  }
`;

const LinkRow = styled.div`
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const FeatureLink = styled(Link)`
  text-decoration: none;
  padding: 10px 12px;
  border-radius: 999px;
  color: #bae6fd;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.14);
  font-size: 13px;
  font-weight: 800;
`;

const featureGroups = [
  {
    title: "Roadmap clarity and execution",
    text:
      "skilll.dev is built around one active roadmap at a time. Instead of random learning, users follow a clear phase-to-week-to-day system.",
    items: [
      {
        icon: Compass,
        tone: "#7dd3fc",
        title: "Career roadmap generation",
        desc: "Turn career confusion into one practical roadmap matched to role, level, and direction.",
      },
      {
        icon: CheckCircle2,
        tone: "#fbbf24",
        title: "My Roadmap progress",
        desc: "Track current phase, week, and day with visible progress instead of vague learning goals.",
      },
      {
        icon: CalendarClock,
        tone: "#86efac",
        title: "Roadmap calendar view",
        desc: "See roadmap days in calendar format so the execution schedule is easier to scan.",
      },
      {
        icon: ClipboardList,
        tone: "#fca5a5",
        title: "Daily task board",
        desc: "Break the day into actionable tasks and move work forward with a clearer execution flow.",
      },
    ],
    links: [
      { to: "/roadmaps", label: "Developer roadmaps" },
      { to: "/career-clarity-home", label: "Career clarity" },
      { to: "/pricing", label: "Pricing" },
    ],
  },
  {
    title: "AI support and guidance",
    text:
      "The product includes AI systems designed for roadmap execution, daily blockers, and focused next-step help instead of generic chatbot noise.",
    items: [
      {
        icon: Brain,
        tone: "#c4b5fd",
        title: "AI Roadmap Assistant",
        desc: "Ask about today’s roadmap, blockers, priorities, and what to do next based on roadmap context.",
      },
      {
        icon: MessageSquareMore,
        tone: "#7dd3fc",
        title: "AI Mentors",
        desc: "Use mentor-style chats for learning, interview prep, and technical direction with plan-based token access.",
      },
      {
        icon: NotebookPen,
        tone: "#fbbf24",
        title: "Saved notes from AI",
        desc: "Save useful AI replies and important points so learning becomes reusable instead of forgettable.",
      },
    ],
    links: [
      { to: "/pricing", label: "AI mentor pricing" },
      { to: "/support", label: "Support" },
    ],
  },
  {
    title: "Consistency systems and reminders",
    text:
      "skilll.dev also includes systems that help users stay consistent after the roadmap is created, which is where most learners usually drop off.",
    items: [
      {
        icon: CalendarClock,
        tone: "#86efac",
        title: "Google Calendar sync",
        desc: "Push roadmap reminders into Google Calendar so the plan stays visible in the tools people already check.",
      },
      {
        icon: CalendarClock,
        tone: "#fbbf24",
        title: "Daily email reminders",
        desc: "Send daily reminder emails for users who want a stronger consistency loop around their roadmap.",
      },
      {
        icon: Users,
        tone: "#7dd3fc",
        title: "Community progress layer",
        desc: "Share progress, blockers, and updates in a community feed built around momentum and proof of work.",
      },
    ],
    links: [
      { to: "/community", label: "Developer community" },
      { to: "/pricing", label: "Compare plans" },
    ],
  },
  {
    title: "Developer utilities that support learning",
    text:
      "Alongside the roadmap system, the website also includes lightweight developer tools that can attract search traffic and help learners do actual work.",
    items: [
      {
        icon: Code2,
        tone: "#7dd3fc",
        title: "Code Practice workspace",
        desc: "Use a clean editor and output view for focused language practice.",
      },
      {
        icon: FileJson,
        tone: "#fbbf24",
        title: "JSON Formatter",
        desc: "Format, search, inspect, and expand JSON in a developer-friendly interface.",
      },
      {
        icon: ClipboardList,
        tone: "#86efac",
        title: "SQL Practice",
        desc: "Practice queries, create tables, and learn SQL in an interactive practice page.",
      },
    ],
    links: [
      { to: "/code-practice", label: "Code Practice" },
      { to: "/json-formatter", label: "JSON Formatter" },
      { to: "/sql-practice", label: "SQL Practice" },
    ],
  },
];

export default function FeaturesPage() {
  const navigate = useNavigate();

  return (
    <Page>
      <Container>
        <Hero>
          <Eyebrow>
            <Sparkles size={14} />
            skilll.dev Features
          </Eyebrow>
          <Title>All the features inside skilll.dev, explained clearly</Title>
          <Sub>
            skilll.dev is not just a roadmap generator. It combines roadmap
            clarity, AI guidance, reminders, task execution, community, and
            practical developer tools so learners can move from confusion to
            consistent progress.
          </Sub>
          <CTARow>
            <PrimaryCTA onClick={() => navigate("/career-clarity-home")}>
              Create My Roadmap
            </PrimaryCTA>
            <SecondaryCTA to="/pricing">Compare Plans</SecondaryCTA>
          </CTARow>
        </Hero>

        {featureGroups.map((group) => (
          <Section key={group.title}>
            <SectionTitle>{group.title}</SectionTitle>
            <SectionText>{group.text}</SectionText>

            <Grid>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <Card key={item.title}>
                    <CardTop>
                      <IconWrap $tone={item.tone}>
                        <Icon size={18} />
                      </IconWrap>
                      <span>{item.title}</span>
                    </CardTop>
                    <CardText>{item.desc}</CardText>
                  </Card>
                );
              })}
            </Grid>

            <LinkRow>
              {group.links.map((item) => (
                <FeatureLink key={item.to} to={item.to}>
                  {item.label}
                </FeatureLink>
              ))}
            </LinkRow>
          </Section>
        ))}

        <Section>
          <SectionTitle>Why this page exists</SectionTitle>
          <SectionText>
            This page helps users understand what skilll.dev includes before
            they start, and it also gives the website a dedicated feature list
            page that can rank for searches around roadmap tools, AI roadmap
            assistants, calendar sync, developer reminders, JSON formatting,
            SQL practice, and coding practice.
          </SectionText>

          <BulletList>
            <li>
              <CheckCircle2 size={16} />
              <span>One page for roadmap, AI, reminder, and utility features.</span>
            </li>
            <li>
              <CheckCircle2 size={16} />
              <span>Clear internal links to important product pages.</span>
            </li>
            <li>
              <CheckCircle2 size={16} />
              <span>Useful copy for both users and search engines.</span>
            </li>
          </BulletList>
        </Section>
      </Container>
    </Page>
  );
}
